import React from 'react'
import { StyledContainer, StyledControl, StyledTopRow } from './styles'
import { RightArrowButton, BoostModeSwitch, WallboxWidgetHeader } from '../../shared'
import { Box } from '@mui/material'
import ChargingProgressBar from 'src/Components/charging-progress-bar/ChargingProgressBar'
import ToggleChargingSessionStatusButton from '../../wallbox/ToggleChargingSessionStatusButton'
import VehicleWidgetInformation from './VehicleWidgetInformation'
import { useParams } from 'react-router-dom'
import { useVehicleImage } from 'src/Libs/e-mobility/vehicle-integration/hooks'
import { ChargingStatus } from 'src/Routes/EMobility/EMobilityModels'
import { isUndefined } from 'lodash'
import { VehicleChargingState } from 'src/Libs/e-mobility/vehicle-integration'
import { VehicleWidgetData } from 'src/Libs/dashboard-widgets/types'
import { MAX_CHARGE_LIMIT_VALUE } from 'src/Libs/e-mobility/vehicle-integration/consts'

interface Props {
  vehicle: VehicleWidgetData
  isOffline?: boolean
  wallboxType: number
  isServerOffline: boolean
  isDynamicPowerPriceEnabledForWallboxes: boolean
  isChargingSessionPaused?: boolean
  currentPowerPrice?: number
  chargingStatus?: ChargingStatus
  energyAll?: number
  toggleWallboxChargingStatus: () => Promise<void>
  isSunModeActive: boolean | undefined
  isBoostModeActive: boolean | undefined
  hasSmartComponent: boolean | undefined
  wallboxStorageId: string | number
  wallboxIndex: number
}

/**
 * component to show vehicle information in dashboard widget
 */
const VehicleWidget: React.FC<Props> = ({
  vehicle,
  isOffline = false,
  wallboxType,
  isServerOffline,
  isDynamicPowerPriceEnabledForWallboxes,
  isChargingSessionPaused,
  currentPowerPrice,
  chargingStatus: wallboxChargingStatus,
  energyAll,
  toggleWallboxChargingStatus,
  isSunModeActive,
  isBoostModeActive,
  hasSmartComponent,
  wallboxStorageId,
  wallboxIndex,
}) => {
  const { vehicleName, stateOfCharge, link, vehicleId, customName, activeSchedule, targetStateOfCharge, chargeLimitMax } = vehicle
  const vehicleIdString = vehicleId ? String(vehicleId) : undefined

  const { installationId } = useParams<{ installationId: string }>()
  const { imageUrl: vehicleImageUrl, loading: isVehicleImageLoading } = useVehicleImage(installationId, vehicleIdString)

  const hasLink = !!link
  const showRightArrowButton = hasLink && !isOffline
  const isBiDi = wallboxType === 5
  const showToggleSunMode = !isBiDi
  const isStopped = wallboxChargingStatus !== 'CHARGING' && wallboxChargingStatus !== 'DISCHARGING'
  const showDynamicPrices = currentPowerPrice !== undefined && isDynamicPowerPriceEnabledForWallboxes && energyAll !== undefined
  const showDynamicPricesCard = !isOffline && showDynamicPrices
  const showToggleChargingSessionStatusButton = !isBiDi && !isUndefined(wallboxChargingStatus)

  return (
    <StyledContainer hasLink={hasLink} isOffline={isOffline}>
      <StyledTopRow>
        <WallboxWidgetHeader
          title={customName ?? vehicleName}
          isOffline={isOffline}
          isChargingSessionPaused={isChargingSessionPaused}
          chargingStatus={wallboxChargingStatus}
          energyAll={energyAll}
          showDynamicPricesIcon={showDynamicPricesCard}
          isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
          isCharging={wallboxChargingStatus === 'CHARGING'}
          isPriceBelowLimit={true}
        />
        <StyledControl>
          {showToggleSunMode && (
            <BoostModeSwitch
              isOffline={isOffline}
              isServerOffline={isServerOffline}
              isSunModeActive={isSunModeActive}
              isBoostModeActive={isBoostModeActive}
              hasSmartComponent={hasSmartComponent}
              wallboxIndex={wallboxIndex}
              wallboxStorageId={wallboxStorageId}
              isFlowEVChargingStrategyBoostMode={!isSunModeActive}
            />
          )}

          {showRightArrowButton && <RightArrowButton link={link} />}
        </StyledControl>
      </StyledTopRow>

      <VehicleWidgetInformation stateOfCharge={stateOfCharge} vehicleImageUrl={vehicleImageUrl} isVehicleImageLoading={isVehicleImageLoading} />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <ChargingProgressBar
          disabled={isOffline}
          discharging={wallboxChargingStatus === 'DISCHARGING'}
          currentChargeInPercentage={stateOfCharge}
          chargeToInPercentage={activeSchedule?.targetStateOfCharge ?? targetStateOfCharge}
          stopped={isChargingSessionPaused || isStopped}
          hasChargingError={vehicle.chargingState === VehicleChargingState.fault}
          markerInPercentage={chargeLimitMax === MAX_CHARGE_LIMIT_VALUE ? undefined : chargeLimitMax}
        />
        {showToggleChargingSessionStatusButton && (
          <ToggleChargingSessionStatusButton
            chargingStatus={wallboxChargingStatus}
            isChargingSessionPaused={isChargingSessionPaused}
            toggleWallboxChargingStatus={toggleWallboxChargingStatus}
          />
        )}
      </Box>
    </StyledContainer>
  )
}

export default VehicleWidget
